import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Pencil, Sun, Moon, Monitor, LogOut, ChevronRight, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import api from '@utils/api';
import { useTheme } from '@context/ThemeContext';
import SettingsSection from './SettingsSection';
import ToggleItem from './ToggleItem';
import SegmentControl from './SegmentControl';

const NOTIF_DEFAULTS = {
  activityUpdates: true,
  memoryRequests: true,
  messages: true,
  marketing: false,
};

const SettingsDrawer = ({
  open,
  onClose,
  profileVisibility,
  onVisibilityChange,
  visibilitySaving,
  onEditName,
  onEditDetails,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();
  const [entered, setEntered] = useState(false);
  const [memorySettings, setMemorySettings] = useState({
    allowPhotosOnProfile: true,
    autoAcceptSharedMemories: false,
  });
  const [memorySaving, setMemorySaving] = useState(false);
  const [notifs, setNotifs] = useState(() => {
    try {
      const raw = localStorage.getItem('yaarvi_notif_prefs');
      return raw ? { ...NOTIF_DEFAULTS, ...JSON.parse(raw) } : NOTIF_DEFAULTS;
    } catch {
      return NOTIF_DEFAULTS;
    }
  });

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/memories/me/memory-settings');
        if (!cancelled && data.memorySettings) {
          setMemorySettings({
            allowPhotosOnProfile: data.memorySettings.allowPhotosOnProfile !== false,
            autoAcceptSharedMemories: Boolean(data.memorySettings.autoAcceptSharedMemories),
          });
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const patchMemory = async (body) => {
    setMemorySaving(true);
    try {
      const { data } = await api.patch('/memories/me/memory-settings', body);
      setMemorySettings({
        allowPhotosOnProfile: data.memorySettings.allowPhotosOnProfile !== false,
        autoAcceptSharedMemories: Boolean(data.memorySettings.autoAcceptSharedMemories),
      });
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save');
    } finally {
      setMemorySaving(false);
    }
  };

  const setNotif = (key, value) => {
    setNotifs((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem('yaarvi_notif_prefs', JSON.stringify(next));
      return next;
    });
  };

  /** Close instantly so no invisible overlay can remain in the DOM. */
  const closeNow = () => {
    setEntered(false);
    onClose();
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[1100]" role="presentation">
      <button
        type="button"
        aria-label="Close settings"
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={closeNow}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        className={`absolute inset-y-0 right-0 z-10 flex w-full max-w-md flex-col bg-[#F7F8FC] shadow-2xl transition-transform duration-300 ease-out dark:bg-[#070B16] sm:max-w-[420px] ${
          entered ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-start justify-between px-5 pb-2 pt-6 sm:px-6">
          <div>
            <h2 id="settings-title" className="text-2xl font-bold text-surface-900 dark:text-white">
              Settings
            </h2>
            <p className="mt-1 text-sm text-surface-500 dark:text-[#B5BDD1]">
              Manage your profile, privacy, and appearance.
            </p>
          </div>
          <button
            type="button"
            onClick={closeNow}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-surface-500 shadow-sm transition hover:bg-surface-50 dark:bg-[#121826] dark:text-[#B5BDD1]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 pb-10 pt-4 sm:px-6">
          <SettingsSection
            title="Shared Memories"
            description="Control how activity photos appear on your profile."
          >
            <ToggleItem
              title="Allow photos on my profile"
              description="Others can see your accepted memories"
              checked={memorySettings.allowPhotosOnProfile}
              disabled={memorySaving}
              onChange={(v) => patchMemory({ allowPhotosOnProfile: v })}
            />
            <div className="border-t border-[#E9EEF7] dark:border-white/[0.06]" />
            <ToggleItem
              title="Auto-accept shared memories"
              description="Skip the accept step when friends share"
              checked={memorySettings.autoAcceptSharedMemories}
              disabled={memorySaving}
              onChange={(v) => patchMemory({ autoAcceptSharedMemories: v })}
            />
          </SettingsSection>

          <SettingsSection
            title="Profile visibility"
            description="Who can view your joined vibes and reviews."
          >
            <SegmentControl
              value={profileVisibility}
              disabled={visibilitySaving}
              onChange={onVisibilityChange}
              options={[
                { value: 'public', label: 'Public' },
                { value: 'private', label: 'Private' },
              ]}
            />
          </SettingsSection>

          <SettingsSection
            title="Edit profile"
            description="Update your name, bio, and interests."
          >
            <div className="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={() => {
                  closeNow();
                  onEditName();
                }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#E9EEF7] bg-white px-3 py-2.5 text-sm font-semibold text-surface-800 transition hover:bg-surface-50 dark:border-white/[0.08] dark:bg-[#1B2435] dark:text-white"
              >
                <Pencil size={14} />
                Name
              </button>
              <button
                type="button"
                onClick={() => {
                  closeNow();
                  onEditDetails();
                }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#E9EEF7] bg-white px-3 py-2.5 text-sm font-semibold text-surface-800 transition hover:bg-surface-50 dark:border-white/[0.08] dark:bg-[#1B2435] dark:text-white"
              >
                <Pencil size={14} />
                Details
              </button>
            </div>
          </SettingsSection>

          <SettingsSection title="Appearance" description="Switch the app appearance.">
            <SegmentControl
              value={mode}
              onChange={setMode}
              options={[
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
                { value: 'system', label: 'System' },
              ]}
            />
            <div className="mt-3 flex items-center gap-3 rounded-2xl bg-surface-50 px-3 py-3 dark:bg-[#1B2435]/60">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-500 dark:bg-brand-500/15">
                {mode === 'dark' ? <Moon size={18} /> : mode === 'system' ? <Monitor size={18} /> : <Sun size={18} />}
              </span>
              <div>
                <p className="text-sm font-semibold text-surface-900 dark:text-white">
                  {mode === 'dark' ? 'Dark Mode' : mode === 'system' ? 'System' : 'Light Mode'}
                </p>
                <p className="text-xs text-surface-500 dark:text-[#B5BDD1]">
                  {mode === 'system' ? 'Follows device setting' : 'Switch the app appearance'}
                </p>
              </div>
            </div>
          </SettingsSection>

          <SettingsSection title="Notifications" description="Choose what you want to hear about.">
            <ToggleItem
              title="Activity Updates"
              description="Joins, reminders, and vibe updates"
              checked={notifs.activityUpdates}
              onChange={(v) => setNotif('activityUpdates', v)}
            />
            <div className="border-t border-[#E9EEF7] dark:border-white/[0.06]" />
            <ToggleItem
              title="Memory Requests"
              description="When someone shares photos with you"
              checked={notifs.memoryRequests}
              onChange={(v) => setNotif('memoryRequests', v)}
            />
            <div className="border-t border-[#E9EEF7] dark:border-white/[0.06]" />
            <ToggleItem
              title="Messages"
              description="Chat and conversation alerts"
              checked={notifs.messages}
              onChange={(v) => setNotif('messages', v)}
            />
            <div className="border-t border-[#E9EEF7] dark:border-white/[0.06]" />
            <ToggleItem
              title="Marketing"
              description="Tips, product news, and offers"
              checked={notifs.marketing}
              onChange={(v) => setNotif('marketing', v)}
            />
          </SettingsSection>

          <SettingsSection title="Privacy">
            <button
              type="button"
              onClick={() => toast('Coming soon')}
              className="flex w-full items-center justify-between py-3 text-left"
            >
              <span className="text-sm font-semibold text-surface-900 dark:text-white">Blocked Users</span>
              <ChevronRight size={18} className="text-surface-400" />
            </button>
            <div className="border-t border-[#E9EEF7] dark:border-white/[0.06]" />
            <button
              type="button"
              onClick={() => toast('Manage hidden albums from Shared Memories settings')}
              className="flex w-full items-center justify-between py-3 text-left"
            >
              <span className="text-sm font-semibold text-surface-900 dark:text-white">Hidden Memories</span>
              <ChevronRight size={18} className="text-surface-400" />
            </button>
            <div className="border-t border-[#E9EEF7] dark:border-white/[0.06]" />
            <button
              type="button"
              onClick={() => toast('Coming soon')}
              className="flex w-full items-center justify-between py-3 text-left"
            >
              <span className="text-sm font-semibold text-surface-900 dark:text-white">Manage Permissions</span>
              <ChevronRight size={18} className="text-surface-400" />
            </button>
          </SettingsSection>

          <SettingsSection title="Account">
            <button
              type="button"
              onClick={() => {
                closeNow();
                onLogout();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-[#E9EEF7] bg-white py-3 text-sm font-bold text-red-600 transition hover:bg-red-50 dark:border-white/[0.08] dark:bg-[#1B2435] dark:text-red-400 dark:hover:bg-red-500/10"
            >
              <LogOut size={16} />
              Sign out
            </button>
            <button
              type="button"
              onClick={() => {
                closeNow();
                navigate('/delete-account');
              }}
              className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 py-3 text-sm font-bold text-white transition hover:bg-red-700 active:scale-[0.99]"
            >
              <Trash2 size={16} />
              Delete Account
            </button>
          </SettingsSection>
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default SettingsDrawer;
